import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft, X, ChevronLeft, ChevronRight, Layers, Tag, Globe2, TrendingUp,
  TrendingDown, ImageOff, Heart, ShoppingCart, DollarSign, Loader2, RefreshCw,
  ExternalLink,
} from "lucide-react";
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { PriceBasisBadge, type PriceBasis } from "@/components/PriceBasisBadge";
import { Button } from "@/components/ui/button";
import { Empty } from "@/components/ui/empty";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SkeletonRows } from "@/components/ui/skeleton";
import { PriceChart } from "@/components/PriceChart";
import { TradeGridDialog } from "@/components/TradeGridDialog";
import { StateBadge } from "@/components/StateBadge";
import { api, ApiError, type PriceRow, type ListingRow, type WTBRow, type FavoriteRow } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth, shortAddr } from "@/lib/auth";
import { cn } from "@/lib/cn";

type Detail = {
  card?: {
    id?: string;
    metadata_name?: string | null;
    card_name?: string | null;
    set_name?: string | null;
    set_code?: string | null;
    card_number?: string | null;
    number?: string | null;
    language?: string | null;
    metadata_rarity?: string | null;
    card_rarity?: string | null;
    artist_name?: string | null;
    hp?: string | number | null;
    average_price?: number | null;
    // card.price : prix de carte quand price_average manque (même valeur sinon).
    card_price?: number | string | null;
    // PO-54 option B : marque du prix de carte, vérifiée sur le prix stocké (raw = calcul
    // brut ; stale = ventes brutes présentes mais prix non recalculé dessus ; fallback_no_raw =
    // ancien prix conservé faute de vente brute ; null = aucun prix).
    price_basis?: PriceBasis;
    raw_price_count?: number | null;
    min_price?: number | null;
    max_price?: number | null;
    median_price?: number | null;
    price_count?: number | null;
    average_30d?: number | null;
    average_7d?: number | null;
    average_1d?: number | null;
    c3?: number | null;
    c3_offer_count?: number | null;
    c3_captured_at?: string | null;
    // PO-76 : C3 = prix principal (C3 retenu, sinon prix de carte).
    c3_offers_count?: number | null;
    c3_occurrences?: number | null;
    c3_stale?: boolean | null;
    c3_max_age_days?: number | null;
    primary_price?: number | null;
    primary_price_source?: "c3" | "card_price" | "computed" | "card_price_stale" | "none" | null;
    primary_price_as_of?: string | null;
    price_last_update?: string | null;
    image_url?: string | null;
    metadata_image_url?: string | null;
    image_is_en_filler?: boolean;
  };
  cardnexusPrices?: CardNexusPrice[];
  variants?: CardVariant[];
  // PO-27 : tendance 90 j + fiabilité de l'estimation (r²), cf. /price_api.
  // PO-54 : `graded` déprécié (toujours vide) ; gradé par (société, note) dans graded_by_grade.
  trend?: {
    graded?: PriceTrend | null;
    ungraded?: PriceTrend | null;
    graded_by_grade?: Record<string, PriceTrend> | null;
  } | null;
};

type PriceTrend = {
  points_90d?: number | null;
  slope_per_day?: number | null;
  price_30d?: number | null;
  price_60d?: number | null;
  price_90d?: number | null;
  r2?: number | null;
  fiability_pct?: number | null;
};

type CardVariantSource = {
  source: string;
  s3: number;
  relevant_count: number;
};

type VariantGradedBucket = {
  grader?: string | null;
  grade?: string | null;
  qualified?: boolean;
  sources?: CardVariantSource[];
  price?: number | null;
  currency?: string | null;
  c3?: number | null;
  c3_offer_count?: number | null;
  c3_captured_at?: string | null;
};

type CardVariant = {
  card_variant_id: string;
  label_fr: string;
  label_en: string;
  // PO-54 : raw = prix brut seul ; graded = variante de dimension grade (prix de son unique bucket).
  price_basis?: "raw" | "graded";
  price_bucket?: string | null;
  sources?: CardVariantSource[];
  price?: number | null;
  currency?: string | null;
  c3?: number | null;
  c3_offer_count?: number | null;
  c3_captured_at?: string | null;
  graded_by_grade?: Record<string, VariantGradedBucket> | null;
};

const GENERIC_GRADED_KEY = "GRADED";

type CardNexusPrice = {
  marketplace: "cardmarket" | "tcgplayer" | "cardnexus" | string;
  currency?: string | null;
  low?: number | string | null;
  mid?: number | string | null;
  high?: number | string | null;
  market_value?: number | string | null;
  change_24h?: number | string | null;
  change_7d?: number | string | null;
  change_30d?: number | string | null;
  listing_count?: number | null;
  finish?: string | null;
  captured_at?: string | null;
};

/**
 * Vue détail d'une carte, réutilisable hors routing.
 * - en page dédiée (/card/:id) : embedded=false → liens vers /card/<id>.
 * - en inline sur l'accueil (?card=<id>) : embedded=true → liens vers /?card=<id>,
 *   bouton « fermer » qui revient à l'accueil nu.
 */
/**
 * PO-24 : fallback EN (porté de public/card.html). `<LANG>_<set>_<num>` absent
 * ⇒ on tente `EN_<set>_<num>`. Pas de fallback vers une autre langue, pas de cache.
 */
function buildEnFallbackId(id: string): { fallbackId: string; originalLang: string } | null {
  const m = String(id || "").match(/^([A-Za-z]{2,3})(_.+)$/);
  if (!m) return null;
  const lang = m[1].toUpperCase();
  if (lang === "EN") return null;
  return { fallbackId: `EN${m[2]}`, originalLang: lang };
}

export function CardDetailView({ cardId: requestedId, embedded = false }: { cardId: string; embedded?: boolean }) {
  const { fmtMoney, fmtDate, t } = useI18n();
  const { wallet, connect } = useAuth();
  const navigate = useNavigate();

  const cardHref = useCallback(
    (id: string) => (embedded ? `/?card=${encodeURIComponent(id)}` : `/card/${encodeURIComponent(id)}`),
    [embedded],
  );

  // Id effectivement affiché : l'id demandé, ou sa version EN si absent (PO-24).
  const [cardId, setCardId] = useState(requestedId);
  const [fallbackLang, setFallbackLang] = useState<string | null>(null);
  // PO-26 : fiche trouvée dans la langue mais sans image ⇒ image de EN_<set>_<num>.
  const [enImageUrl, setEnImageUrl] = useState<string | null>(null);
  const [detail, setDetail] = useState<Detail | null>(null);
  const [prices, setPrices] = useState<PriceRow[]>([]);
  const [listings, setListings] = useState<ListingRow[]>([]);
  const [wtb, setWtb] = useState<WTBRow[]>([]);
  const [adjacent, setAdjacent] = useState<{ prev?: string | null; next?: string | null }>({});
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [favBusy, setFavBusy] = useState(false);
  const [tradeMode, setTradeMode] = useState<"buy" | "sell" | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      // Détail d'abord : 404 ou corps vide ⇒ fallback EN (PO-24).
      let id = requestedId;
      let fbLang: string | null = null;
      let d: Detail | null = null;
      try {
        d = await api<Detail>(`/api/cards/${encodeURIComponent(id)}/detail`);
      } catch (e) {
        if (!(e instanceof ApiError && e.status === 404)) throw e;
      }
      if (!d || !d.card) {
        const fb = buildEnFallbackId(requestedId);
        if (!fb) throw new Error("Card not found");
        try {
          d = await api<Detail>(`/api/cards/${encodeURIComponent(fb.fallbackId)}/detail`);
        } catch (e) {
          if (e instanceof ApiError && e.status === 404) throw new Error("Card not found");
          throw e;
        }
        if (!d || !d.card) throw new Error("Card not found");
        id = fb.fallbackId;
        fbLang = fb.originalLang;
      }
      // PO-26 : carte présente dans la langue mais sans image ⇒ image de la
      // variante EN (si elle en a une). Échec du fetch EN ⇒ pas d'image, comme avant.
      let enImg: string | null = null;
      if (!fbLang && !(d.card?.image_url || d.card?.metadata_image_url)) {
        const fb = buildEnFallbackId(requestedId);
        if (fb) {
          try {
            const en = await api<Detail>(`/api/cards/${encodeURIComponent(fb.fallbackId)}/detail`);
            enImg = en?.card?.image_url || en?.card?.metadata_image_url || null;
          } catch {
            enImg = null;
          }
        }
      }
      const cardId = id;
      const [p, l, b, adj] = await Promise.all([
        api<PriceRow[]>(`/api/cards/${encodeURIComponent(cardId)}/prices?limit=400`),
        api<{ items: ListingRow[] }>(`/api/listings?card_id=${encodeURIComponent(cardId)}`).catch(() => ({ items: [] as ListingRow[] })),
        api<{ items: WTBRow[] }>(`/api/wtb?card_id=${encodeURIComponent(cardId)}`).catch(() => ({ items: [] as WTBRow[] })),
        api<{ prev?: string | null; next?: string | null }>(`/api/cards/${encodeURIComponent(cardId)}/adjacent`).catch(() => ({})),
      ]);
      setCardId(cardId);
      setFallbackLang(fbLang);
      setEnImageUrl(enImg);
      setDetail(d);
      setPrices(Array.isArray(p) ? p : []);
      setListings(l.items ?? []);
      setWtb(b.items ?? []);
      setAdjacent(adj);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [requestedId]);

  const refreshFavorite = useCallback(async () => {
    if (!wallet) {
      setIsFavorite(false);
      return;
    }
    try {
      const r = await api<{ items: FavoriteRow[] }>("/api/me/favorites");
      setIsFavorite((r.items || []).some((f) => f.card_id === cardId));
    } catch {
      setIsFavorite(false);
    }
  }, [wallet, cardId]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    refreshFavorite();
  }, [refreshFavorite]);

  const toggleFavorite = async () => {
    if (!wallet) return;
    setFavBusy(true);
    try {
      if (isFavorite) {
        await api(`/api/me/favorites/${encodeURIComponent(cardId)}`, { method: "DELETE" });
      } else {
        await api("/api/me/favorites", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ cardId }),
        });
      }
      setIsFavorite((b) => !b);
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setFavBusy(false);
    }
  };

  // PO-55 : message lisible pour 401 (session expirée) et 429 (plafond, délai
  // tiré de Retry-After) ; les autres erreurs gardent le texte du serveur.
  // t() rend la clé quand la traduction manque : repli explicite.
  const tr = (key: string, fallback: string, vars?: Record<string, string | number>) => {
    const s = t(key, vars);
    return s && s !== key ? s : fallback;
  };
  const refreshErrorMessage = (r: Response, body: { error?: string; reason?: string }): string => {
    if (r.status === 401) {
      return tr("card.refresh_login", "Session expirée : reconnectez votre wallet pour rafraîchir.");
    }
    if (r.status === 429) {
      const secs = Number(r.headers.get("Retry-After"));
      const min = Number.isFinite(secs) && secs > 0 ? Math.ceil(secs / 60) : null;
      const why =
        body.reason === "set_cooldown"
          ? tr("card.refresh_set_cooldown", "Ce set vient d'être rafraîchi.")
          : body.reason === "global"
            ? tr("card.refresh_global_limit", "Trop de rafraîchissements en cours sur le site.")
            : tr("card.refresh_wallet_limit", "Limite de rafraîchissements atteinte pour votre wallet.");
      return min ? `${why} ${tr("card.refresh_retry_in", `Réessayez dans ${min} min.`, { min })}` : why;
    }
    return body.error || `HTTP ${r.status}`;
  };

  // Rafraîchissement à la demande : source le set entier de la carte (async),
  // puis poll le statut du job jusqu'à inactivité et recharge la fiche.
  const refreshPrice = useCallback(async () => {
    setRefreshing(true);
    setRefreshMsg(t("card.refreshing") || "Refreshing the whole set…");
    try {
      const r = await fetch(`/api/cards/${encodeURIComponent(cardId)}/refresh`, {
        method: "POST",
        credentials: "same-origin",
      });
      const body = (await r.json().catch(() => ({}))) as { error?: string; set?: string; reason?: string };
      if (r.status !== 202) {
        setRefreshMsg(refreshErrorMessage(r, body));
        setRefreshing(false);
        return;
      }
      const startedAt = Date.now();
      const poll = async () => {
        try {
          const s = await api<{ running?: boolean }>("/api/jobs/price-update");
          if (!s.running || Date.now() - startedAt > 120_000) {
            await load();
            setRefreshing(false);
            setRefreshMsg(t("card.refresh_done") || "Prices refreshed.");
            return;
          }
        } catch {
          /* transient — on retente */
        }
        window.setTimeout(poll, 2500);
      };
      window.setTimeout(poll, 2500);
    } catch (e) {
      setRefreshMsg((e as Error).message);
      setRefreshing(false);
    }
  }, [cardId, load, t]);

  const c = detail?.card ?? {};
  const name = c.metadata_name || c.card_name || "—";
  const rarity = c.metadata_rarity || c.card_rarity;
  const setLabel = c.set_name || c.set_code;
  const number = c.card_number || c.number;
  const ownImageUrl = c.image_url || c.metadata_image_url;
  const imageUrl = ownImageUrl || enImageUrl;
  const showEnImageFallback = !ownImageUrl && !!enImageUrl;

  // Meilleur prix de chaque côté (style bourse) : best bid = WTB le plus haut,
  // best ask = WTS le plus bas. Affichés sur les boutons BUY/SELL.
  const bestBid = wtb.length ? Math.max(...wtb.map((w) => Number(w.price_usdc) || 0)) : null;
  const bestAsk = listings.length
    ? Math.min(...listings.map((l) => Number(l.price_usdc) || Infinity))
    : null;

  // Chart data : reduce prices to median-per-day points sorted by time.
  const chartData = useMemo(() => {
    const buckets = new Map<string, number[]>();
    for (const row of prices) {
      const first = row.price_date || row.created_at;
      if (!first) continue;
      const price = Number(row.price);
      if (!Number.isFinite(price) || price <= 0) continue;
      // PO-65 : une ligne vaut de sa première à sa dernière fois vue (last_seen_at) ⇒ un point
      // à chaque extrémité (bucket par jour UTC), sinon une série stable s'arrête à sa création.
      const last = row.price_date || row.last_seen_at || row.created_at || first;
      const days = new Set([String(first).slice(0, 10), String(last).slice(0, 10)]);
      for (const day of days) {
        const arr = buckets.get(day) ?? [];
        arr.push(price);
        buckets.set(day, arr);
      }
    }
    const points = Array.from(buckets.entries())
      .map(([day, arr]) => {
        arr.sort((a, b) => a - b);
        const median = arr[Math.floor(arr.length / 2)];
        return { t: new Date(day + "T12:00:00Z").getTime(), p: median };
      })
      .sort((a, b) => a.t - b.t);
    return points;
  }, [prices]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        {embedded ? (
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg">
            <X className="h-4 w-4" /> {t("card.close") || "Close card"}
          </Link>
        ) : (
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-fg hover:text-fg">
            <ArrowLeft className="h-4 w-4" /> {t("card.back_to_search") || "Back to search"}
          </Link>
        )}
        <div className="flex items-center gap-1">
          {refreshMsg ? <span className="mr-1 hidden text-xs text-muted-fg sm:inline">{refreshMsg}</span> : null}
          {/* PO-55 : POST /refresh exige une session wallet (401 sinon) ⇒ bouton réservé aux connectés. */}
          {wallet ? (
            <Button
              variant="outline"
              size="sm"
              onClick={refreshPrice}
              disabled={refreshing}
              title={t("card.refresh_hint") || "Refetch this card's whole set from the source"}
            >
              <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
              <span className="hidden sm:inline">{t("card.refresh") || "Refresh price"}</span>
            </Button>
          ) : null}
          <Button variant="ghost" size="icon" className="sm:w-auto sm:px-3" disabled={!adjacent.prev} onClick={() => adjacent.prev && navigate(cardHref(adjacent.prev))}>
            <ChevronLeft className="h-4 w-4" /> <span className="hidden sm:inline">Prev</span>
          </Button>
          <Button variant="ghost" size="icon" className="sm:w-auto sm:px-3" disabled={!adjacent.next} onClick={() => adjacent.next && navigate(cardHref(adjacent.next))}>
            <span className="hidden sm:inline">Next</span> <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {fallbackLang && !err ? (
        <div
          role="status"
          className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/60 dark:bg-amber-500/10 dark:text-amber-200"
        >
          {(() => {
            const vars = { lang: fallbackLang, id: cardId };
            const msg = t("card.en_fallback_banner", vars);
            return msg === "card.en_fallback_banner"
              ? `Visuel en ${fallbackLang} non disponible — affichage de la version EN (${cardId}).`
              : msg;
          })()}
          <span className="ml-1 font-mono text-xs opacity-70">({requestedId} → {cardId})</span>
        </div>
      ) : null}

      {err ? (
        <Card>
          <CardBody><Empty title="Failed to load" description={err} /></CardBody>
        </Card>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[280px_1fr]">
        {/* Left column — card image + meta */}
        <div className="min-w-0 space-y-4">
          <Card className="overflow-hidden">
            <div className="relative grid place-items-center bg-surface-2 p-4">
              {showEnImageFallback ? (
                <span className="absolute right-2 top-2 rounded-full bg-surface-1/90 px-2 py-0.5 text-[10px] font-medium text-muted-fg shadow-sm">
                  {t("card.en_image_fallback")}
                </span>
              ) : imageUrl && c.image_is_en_filler && c.language && c.language !== "EN" ? (
                <span className="absolute right-2 top-2 rounded-full bg-surface-1/90 px-2 py-0.5 text-[10px] font-medium text-muted-fg shadow-sm">
                  {t("card.en_filler_badge")}
                </span>
              ) : null}
              {imageUrl ? (
                <img src={imageUrl} alt={`${name} card`} className="max-h-[360px] w-auto rounded-md" />
              ) : (
                <div className="flex h-[360px] w-full flex-col items-center justify-center gap-2 text-muted-fg">
                  <ImageOff className="h-7 w-7" />
                  <span className="text-xs">No image</span>
                </div>
              )}
            </div>
            <CardBody className="space-y-1.5">
              <div className="text-xs font-mono text-muted-fg">{cardId}</div>
              <div className="text-lg font-semibold leading-snug">{name}</div>
              <div className="flex flex-wrap gap-1.5 pt-2">
                {rarity ? <Badge tone="info">{rarity}</Badge> : null}
                {c.language ? <Badge tone="neutral">{c.language}</Badge> : null}
              </div>
              <ul className="mt-3 space-y-1.5 text-xs text-muted-fg">
                <Meta icon={<Layers className="h-3.5 w-3.5" />} label="Set" value={setLabel} />
                <Meta icon={<Tag className="h-3.5 w-3.5" />} label="Number" value={number} />
                <Meta icon={<Globe2 className="h-3.5 w-3.5" />} label="Language" value={c.language} />
                {c.artist_name ? <Meta label="Artist" value={c.artist_name} /> : null}
                {c.hp != null ? <Meta label="HP" value={String(c.hp)} /> : null}
              </ul>
            </CardBody>
          </Card>

          {/* Trade actions — style bourse : BUY (vert) / SELL (rouge) */}
          <Card>
            <CardBody className="space-y-2 p-3">
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setTradeMode("buy")}
                  className="flex flex-col items-center justify-center rounded-lg bg-success py-3 text-white shadow-soft transition-colors hover:bg-success/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-success/50"
                >
                  <span className="inline-flex items-center gap-1.5 text-lg font-bold tracking-wide">
                    <ShoppingCart className="h-4 w-4" /> BUY
                  </span>
                  <span className="text-[11px] font-medium opacity-90">
                    {bestBid != null ? fmtMoney(bestBid, "USD") : "Placer une offre"}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setTradeMode("sell")}
                  className="flex flex-col items-center justify-center rounded-lg bg-danger py-3 text-white shadow-soft transition-colors hover:bg-danger/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/50"
                >
                  <span className="inline-flex items-center gap-1.5 text-lg font-bold tracking-wide">
                    <DollarSign className="h-4 w-4" /> SELL
                  </span>
                  <span className="text-[11px] font-medium opacity-90">
                    {bestAsk != null ? fmtMoney(bestAsk, "USD") : "Mettre en vente"}
                  </span>
                </button>
              </div>
              {/* PO-28 : favori visible uniquement si wallet connecté. */}
              {wallet ? (
              <Button size="sm" variant="ghost" className="w-full" data-testid="card-fav-toggle" onClick={toggleFavorite} disabled={favBusy}>
                {favBusy ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Heart data-testid="card-fav-heart" className={cn("h-3.5 w-3.5", isFavorite && "fill-danger text-danger")} />
                )}
                {isFavorite ? t("card.action.unfavorite") || "Remove from favorites" : t("card.action.favorite") || "Add to favorites"}
              </Button>
              ) : null}
            </CardBody>
          </Card>
        </div>

        {/* Right column — carnet d'ordres (prominent), stats, chart, tabs */}
        <div className="min-w-0 space-y-6">
          {/* PO-76 : C3 en tête de la fiche, avant le prix de carte et les gradés. */}
          <C3Tile data={c} loading={loading && !detail} fmtMoney={fmtMoney} fmtDate={fmtDate} />

          <Orderbook listings={listings} wtb={wtb} fmtMoney={fmtMoney} loading={loading} />

          <PriceStats data={c} priceTrend={detail?.trend} loading={loading && !detail} fmtMoney={fmtMoney} fmtDate={fmtDate} />

          <VariantsPanel variants={detail?.variants} fmtMoney={fmtMoney} fmtDate={fmtDate} />

          <CardNexusPanel prices={detail?.cardnexusPrices} fmtMoney={fmtMoney} fmtDate={fmtDate} />

          <Card>
            <CardHeader>
              <div>
                <CardTitle>{t("card.chart.title") || "Price history"}</CardTitle>
                <CardDescription>
                  {chartData.length > 0
                    ? `${chartData.length} ${chartData.length > 1 ? "data points" : "data point"} (median per day, all sources)`
                    : "No observations"}
                </CardDescription>
              </div>
            </CardHeader>
            <CardBody>
              <PriceChart
                data={chartData}
                height={240}
                currency={prices[0]?.currency || "EUR"}
                fmtMoney={fmtMoney}
                fmtDate={fmtDate}
              />
            </CardBody>
          </Card>

          <Tabs defaultValue="prices">
            <TabsList>
              <TabsTrigger value="prices">Prices ({prices.length})</TabsTrigger>
              <TabsTrigger value="wts">WTS ({listings.length})</TabsTrigger>
              <TabsTrigger value="wtb">WTB ({wtb.length})</TabsTrigger>
            </TabsList>
            <div className="mt-4">
              <TabsContent value="prices">
                <PricesTable rows={prices} loading={loading} fmtMoney={fmtMoney} fmtDate={fmtDate} />
              </TabsContent>
              <TabsContent value="wts">
                <ListingsTable rows={listings} loading={loading} fmtMoney={fmtMoney} fmtDate={fmtDate} />
              </TabsContent>
              <TabsContent value="wtb">
                <WTBTable rows={wtb} loading={loading} fmtMoney={fmtMoney} fmtDate={fmtDate} />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>

      <TradeGridDialog
        open={tradeMode !== null}
        onClose={() => setTradeMode(null)}
        mode={tradeMode ?? "buy"}
        cardId={cardId}
        cardName={name}
        wallet={wallet}
        onConnect={connect}
        onPosted={() => load()}
      />
    </div>
  );
}

function Meta({ icon, label, value }: { icon?: React.ReactNode; label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <li className="flex items-center gap-2">
      {icon ? <span className="text-muted-fg/70">{icon}</span> : null}
      <span className="w-16 shrink-0 uppercase tracking-wide text-[10px]">{label}</span>
      <span className="font-medium text-fg">{value}</span>
    </li>
  );
}

/* ---------- Variantes (finish/édition) — prix + C3 par variante, jamais blendés (PR-1039) ---------- */
function VariantsPanel({
  variants,
  fmtMoney,
  fmtDate,
}: {
  variants?: CardVariant[];
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  const { t, locale } = useI18n();
  // Point 4 de l'arbitrage Laurent : sans précision, on montre TOUT, C3 par
  // variante, la variante "normale" en premier (listCardVariants la trie déjà
  // ainsi). Affiché même s'il n'y a que la variante de base — c'est
  // désormais le SEUL endroit où le C3 est rendu comme un prix (le C3 legacy
  // blendé de `card.c3` reste en base pour compat mais n'est plus affiché).
  const rows = variants ?? [];
  if (rows.length === 0) return null;

  const label = (v: CardVariant) => (locale === "fr" ? v.label_fr : v.label_en) || v.label_fr || v.label_en;
  const bucketLabel = (key: string, b?: VariantGradedBucket | null) =>
    key === GENERIC_GRADED_KEY || b?.qualified === false
      ? t("card.variants.graded_generic")
      : `${b?.grader ?? key.split("|")[0]} ${b?.grade ?? key.split("|")[1] ?? ""}`.trim();

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t("card.variants.title") || "Variants"}</CardTitle>
          <CardDescription>
            {t("card.variants.desc") ||
              "Each variant has its own S3 per source, C3 and Price — never blended together."}
          </CardDescription>
        </div>
        <Badge tone="warn" variant="soft">{rows.length}</Badge>
      </CardHeader>
      <CardBody>
        <div className="overflow-hidden rounded-md border border-border">
          <Table>
            <THead>
              <TR>
                <TH>{t("card.variants.variant") || "Variant"}</TH>
                <TH>{t("card.variants.sources") || "S3 by source"}</TH>
                <TH className="text-right">{t("card.c3.label") || "C3 price"}</TH>
                <TH className="text-right">{t("card.variants.price") || "Price"}</TH>
              </TR>
            </THead>
            <TBody>
              {rows.flatMap((v) => {
                const buckets = Object.entries(v.graded_by_grade ?? {});
                const note =
                  v.price_basis === "graded"
                    ? v.price_bucket === GENERIC_GRADED_KEY
                      ? t("card.variants.graded_generic")
                      : v.price_bucket
                        ? bucketLabel(v.price_bucket, v.graded_by_grade?.[v.price_bucket])
                        : buckets.length > 1
                          ? t("card.variants.graded_multi")
                          : t("card.variants.graded_none")
                    : null;
                // Le bucket qui porte déjà le prix de la ligne n'est pas répété en sous-ligne.
                const subRows = buckets.filter(([k]) => !(v.price_basis === "graded" && k === v.price_bucket));
                return [
                  <PriceRowCells
                    key={v.card_variant_id}
                    label={label(v)}
                    note={note}
                    row={v}
                    fmtMoney={fmtMoney}
                    fmtDate={fmtDate}
                  />,
                  ...subRows.map(([k, b]) => (
                    <PriceRowCells
                      key={`${v.card_variant_id}:${k}`}
                      label={`↳ ${bucketLabel(k, b)}`}
                      note={null}
                      row={b}
                      fmtMoney={fmtMoney}
                      fmtDate={fmtDate}
                      sub
                    />
                  )),
                ];
              })}
            </TBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}

/* PO-54 : une ligne de prix (variante ou bucket gradé d'une variante). */
function PriceRowCells({
  label,
  note,
  row,
  fmtMoney,
  fmtDate,
  sub = false,
}: {
  label: string;
  note: string | null;
  row: { sources?: CardVariantSource[]; price?: number | null; currency?: string | null; c3?: number | null; c3_captured_at?: string | null };
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
  sub?: boolean;
}) {
  const cur = row.currency || "EUR";
  return (
    <TR>
      <TD className={sub ? "pl-6 text-sm text-muted-fg" : "font-medium"}>
        {label}
        {note ? <div className="text-xs font-normal text-muted-fg">{note}</div> : null}
      </TD>
      <TD className="text-sm text-muted-fg">
        {(row.sources ?? []).length
          ? (row.sources ?? []).map((s) => `${s.source} : ${fmtMoney(s.s3, cur)}`).join(" · ")
          : "—"}
      </TD>
      <TD className="text-right font-mono">
        {row.c3 != null ? (
          <span title={row.c3_captured_at ? fmtDate(row.c3_captured_at) : undefined}>{fmtMoney(row.c3, cur)}</span>
        ) : (
          <span className="text-muted-fg">—</span>
        )}
      </TD>
      <TD className={sub ? "text-right font-mono" : "text-right font-mono font-semibold"}>
        {row.price != null ? fmtMoney(row.price, cur) : "—"}
      </TD>
    </TR>
  );
}

/* ---------- CardNexus aggregates (display only) ---------- */
function CardNexusPanel({
  prices,
  fmtMoney,
  fmtDate,
}: {
  prices?: CardNexusPrice[];
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  const { t } = useI18n();
  const rows = (prices ?? []).filter(
    (p) => p && (p.low != null || p.mid != null || p.market_value != null),
  );
  if (rows.length === 0) return null;

  const order: Record<string, number> = { cardmarket: 0, tcgplayer: 1, cardnexus: 2 };
  const label: Record<string, string> = { cardmarket: "Cardmarket", tcgplayer: "TCGplayer", cardnexus: "CardNexus" };
  const sorted = [...rows].sort((a, b) => (order[a.marketplace] ?? 9) - (order[b.marketplace] ?? 9));
  const lastCap = sorted.map((r) => r.captured_at).filter(Boolean).sort().at(-1) || null;
  const pct = (v: number | string | null | undefined) => {
    const n = Number(v);
    return Number.isFinite(n) ? n * 100 : null;
  };

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>{t("card.cardnexus.title") || "CardNexus prices"}</CardTitle>
          <CardDescription>
            {t("card.cardnexus.desc") || "Aggregated market prices — display only"}
            {lastCap ? ` · ${fmtDate(lastCap)}` : ""}
          </CardDescription>
        </div>
        <Badge tone="info" variant="soft">CardNexus</Badge>
      </CardHeader>
      <CardBody>
        <div className="overflow-hidden rounded-md border border-border">
          <Table>
            <THead>
              <TR>
                <TH>{t("card.cardnexus.market") || "Market"}</TH>
                <TH className="text-right">{t("card.cardnexus.low") || "Low"}</TH>
                <TH className="text-right">{t("card.cardnexus.mid") || "Mid"}</TH>
                <TH className="text-right">{t("card.cardnexus.market_value") || "Market"}</TH>
                <TH className="text-right">30d</TH>
                <TH className="text-right">{t("card.cardnexus.listings") || "Listings"}</TH>
              </TR>
            </THead>
            <TBody>
              {sorted.map((r) => {
                const cur = r.currency || "EUR";
                const p30 = pct(r.change_30d);
                return (
                  <TR key={r.marketplace}>
                    <TD className="font-medium">{label[r.marketplace] || r.marketplace}</TD>
                    <TD className="text-right font-mono">{r.low != null ? fmtMoney(r.low, cur) : "—"}</TD>
                    <TD className="text-right font-mono">{r.mid != null ? fmtMoney(r.mid, cur) : "—"}</TD>
                    <TD className="text-right font-mono">{r.market_value != null ? fmtMoney(r.market_value, cur) : "—"}</TD>
                    <TD className="text-right">
                      {p30 != null ? <TrendArrow pct={p30} /> : <span className="text-muted-fg">—</span>}
                    </TD>
                    <TD className="text-right font-mono text-muted-fg">{r.listing_count ?? "—"}</TD>
                  </TR>
                );
              })}
            </TBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------- PO-76 : tuile C3 (prix principal) ---------- */
function C3Tile({
  data,
  loading,
  fmtMoney,
  fmtDate,
}: {
  data: NonNullable<Detail["card"]>;
  loading: boolean;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  const { t } = useI18n();
  const tr = (key: string, fallback: string, vars?: Record<string, string | number>) => {
    const v = t(key, vars);
    return v === key ? fallback : v;
  };
  const c3 = data.c3 != null && Number.isFinite(Number(data.c3)) ? Number(data.c3) : null;
  const offers = Number(data.c3_offers_count ?? data.c3_offer_count ?? 0);
  const occ = Number(data.c3_occurrences ?? 0);
  const isPrimary = data.primary_price_source === "c3";
  const stale = Boolean(data.c3_stale);
  const noC3 =
    offers > 0
      ? tr("card.c3.none_threshold", `pas de C3 (aucun montant présent 3 fois parmi ${offers} offres)`, { n: offers })
      : tr("card.c3.none", "pas de C3 (aucune capture Cardmarket)");
  return (
    <Card data-testid="c3-tile">
      <CardBody className="flex flex-wrap items-center justify-between gap-3 p-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-wide text-muted-fg" title={t("card.c3.help")}>
              {tr("card.c3.label", "Prix C3")}
            </span>
            {isPrimary ? (
              <Badge tone="success" variant="soft" data-testid="c3-primary">{tr("card.primary.badge", "principal")}</Badge>
            ) : null}
            {c3 != null && stale ? (
              <Badge tone="warn" variant="soft" data-testid="c3-stale">
                {tr("card.c3.stale", "capture ancienne")}
              </Badge>
            ) : null}
          </div>
          <div className="mt-1 text-2xl font-semibold tabular-nums" data-testid="c3-value">
            {loading ? <span className="skeleton inline-block h-6 w-24 rounded" /> : c3 != null ? fmtMoney(c3) : <span className="text-base font-normal text-muted-fg">{noC3}</span>}
          </div>
          {!loading && c3 != null ? (
            <div className="mt-1 text-xs text-muted-fg" data-testid="c3-meta">
              {tr("card.c3.captured", `capture du ${fmtDate(data.c3_captured_at)}`, { date: fmtDate(data.c3_captured_at) })}
              {" · "}
              {tr("card.c3.offers_n", `${offers} offres, ${occ} au montant C3`, { n: offers, k: occ })}
            </div>
          ) : null}
        </div>
        {!loading && !isPrimary ? (
          <div className="text-right text-xs text-muted-fg" data-testid="primary-fallback">
            {(data.primary_price_source === "card_price" || data.primary_price_source === "computed") && data.primary_price != null ? (
              <>
                <div>
                  {data.primary_price_source === "computed"
                    ? tr("card.primary.computed", "Prix principal : prix de carte recalculé (relevés actuels)")
                    : tr("card.primary.card_price", "Prix principal : prix de carte")}
                </div>
                <div className="text-base font-semibold text-fg tabular-nums">{fmtMoney(data.primary_price)}</div>
                {data.primary_price_as_of ? <div>{fmtDate(data.primary_price_as_of)}</div> : null}
              </>
            ) : (
              <div>{tr("card.primary.none", "Aucun prix principal")}</div>
            )}
          </div>
        ) : null}
      </CardBody>
    </Card>
  );
}

/* ---------- Stats with trend arrows ---------- */
function PriceStats({
  data,
  priceTrend,
  loading,
  fmtMoney,
  fmtDate,
}: {
  data: NonNullable<Detail["card"]>;
  priceTrend?: Detail["trend"];
  loading: boolean;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  const { t } = useI18n();
  const fmt = (v: number | null | undefined) => (v == null ? "—" : fmtMoney(v));
  // PO-54 : prix de carte = average_price, sinon card.price (même repli que la marque serveur).
  const cardPrice = data.average_price ?? (data.card_price != null && data.card_price !== "" ? Number(data.card_price) : null);
  const base = Number(cardPrice ?? 0);
  const trend = (val: number | null | undefined) => {
    if (val == null || !Number.isFinite(Number(val)) || !base) return null;
    return ((Number(val) - base) / base) * 100;
  };
  const stats = [
    { label: "Average", value: fmt(cardPrice), trend: null as number | null, tone: "primary" as const },
    { label: "Min", value: fmt(data.min_price), trend: null },
    { label: "Max", value: fmt(data.max_price), trend: null },
    { label: "Median", value: fmt(data.median_price), trend: null },
    { label: "30d avg", value: fmt(data.average_30d), trend: trend(data.average_30d) },
    { label: "7d avg", value: fmt(data.average_7d), trend: trend(data.average_7d) },
  ];
  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Prices overview</CardTitle>
          <CardDescription>
            {data.price_count ? `${data.price_count} observations` : "—"}
            {data.price_last_update ? ` · last update ${fmtDate(data.price_last_update)}` : ""}
          </CardDescription>
        </div>
        <Badge tone="success" variant="soft">
          <TrendingUp className="h-3 w-3" /> live
        </Badge>
      </CardHeader>
      <CardBody className="space-y-3">
        {/* Le C3 (plancher fiable Cardmarket) ne s'affiche plus ici de façon
            blendée toutes variantes confondues — cf. VariantsPanel, qui montre
            le C3 PAR variante (normal en premier). card.c3 legacy reste en
            base pour compat mais n'est plus rendu comme un prix (PR-1039,
            arbitrage Laurent point 4). */}
        <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
          {stats.map((s) => (
            <div key={s.label} className="rounded-md border border-border bg-surface p-3">
              <div className="flex items-center justify-between gap-2">
                <div className="text-[10px] uppercase tracking-wide text-muted-fg">{s.label}</div>
                {s.trend != null ? (
                  <TrendArrow pct={s.trend} />
                ) : null}
              </div>
              <div className="mt-1 text-base font-semibold tabular-nums">
                {loading ? <span className="skeleton inline-block h-4 w-16 rounded" /> : s.value}
              </div>
              {!loading && s.label === "Average" && cardPrice != null && data.price_basis ? (
                <div className="mt-1">
                  <PriceBasisBadge basis={data.price_basis} />
                </div>
              ) : null}
              {/* PO-76 : sans C3 retenu, le prix de carte est le prix principal. */}
              {!loading && s.label === "Average" && data.primary_price_source === "card_price" ? (
                <div className="mt-1">
                  <Badge tone="success" variant="soft" data-testid="card-price-primary">{t("card.primary.badge") === "card.primary.badge" ? "principal" : t("card.primary.badge")}</Badge>
                </div>
              ) : null}
            </div>
          ))}
        </div>
        {!loading ? <ProjectionRow trend={priceTrend} fmtMoney={fmtMoney} /> : null}
      </CardBody>
    </Card>
  );
}

/* ---------- PO-27 : projections linéaires 30/60/90 j + fiabilité (r² × 100, fenêtre 90 j) ---------- */
function ProjectionRow({
  trend,
  fmtMoney,
}: {
  trend?: Detail["trend"];
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
}) {
  const { t, fmtNumber } = useI18n();
  const rows: { key: string; label: string; tr: PriceTrend }[] = [];
  const ungraded = trend?.ungraded;
  // Non gradé toujours affiché (n/d sans points) ; gradé : une ligne par (société, note)
  // ayant des points (PO-54 : `graded` mêlait tous gradeurs, déprécié et toujours vide).
  rows.push({ key: "ungraded", label: t("card.fiability.ungraded"), tr: ungraded ?? {} });
  for (const [k, tr] of Object.entries(trend?.graded_by_grade ?? {})) {
    if (!tr || Number(tr.points_90d || 0) <= 0) continue;
    const label =
      k === "GRADED"
        ? t("card.fiability.graded_generic")
        : `${t("card.fiability.graded")} ${k.replace("|", " ")}`;
    rows.push({ key: `graded:${k}`, label, tr });
  }
  const na = t("card.fiability.na");
  const fmtPct = (v: number | null | undefined) => {
    if (v == null || !Number.isFinite(Number(v))) return na;
    const n = Math.max(0, Math.min(100, Number(v)));
    return `${fmtNumber(n, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
  };
  const fmtProj = (v: number | null | undefined) =>
    v == null || !Number.isFinite(Number(v)) ? na : fmtMoney(v);
  const horizons: { key: "price_30d" | "price_60d" | "price_90d"; label: string }[] = [
    { key: "price_30d", label: t("card.projection.d30") },
    { key: "price_60d", label: t("card.projection.d60") },
    { key: "price_90d", label: t("card.projection.d90") },
  ];
  return (
    <div className="space-y-2 rounded-md border border-border bg-surface px-3 py-2 text-sm" data-testid="projection">
      <div className="text-[10px] uppercase tracking-wide text-muted-fg" title={t("card.projection.hint")}>
        {t("card.projection.label")}
      </div>
      {rows.map((r) => (
        <div
          key={r.key}
          className="flex flex-wrap items-center gap-x-4 gap-y-1 tabular-nums"
          data-projection={r.key}
        >
          <span className="min-w-[5rem] text-muted-fg">{r.label}</span>
          {horizons.map((h) => (
            <span key={h.key}>
              <span className="text-xs text-muted-fg">{h.label} </span>
              <span className="font-semibold" data-proj={h.key}>{fmtProj(r.tr[h.key])}</span>
            </span>
          ))}
          <span title={t("card.fiability.hint")} data-testid="fiability">
            <span className="text-xs text-muted-fg">{t("card.fiability.label")} </span>
            <span className="font-semibold" data-fiability={r.key}>{fmtPct(r.tr.fiability_pct)}</span>
            <span className="ml-1 text-xs text-muted-fg">
              ({t("card.fiability.points", { n: Number(r.tr.points_90d || 0) })})
            </span>
          </span>
        </div>
      ))}
    </div>
  );
}

function TrendArrow({ pct }: { pct: number }) {
  const isUp = pct > 0.5;
  const isDown = pct < -0.5;
  const tone: "success" | "danger" | "neutral" = isUp ? "success" : isDown ? "danger" : "neutral";
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : null;
  const sign = isUp ? "+" : "";
  return (
    <Badge tone={tone} variant="soft" className="font-mono text-[10px]">
      {Icon ? <Icon className="h-2.5 w-2.5" /> : null}
      {sign}{pct.toFixed(1)}%
    </Badge>
  );
}

/* ---------- Orderbook — un carnet PAR ÉTAT (loose ou gradée) ---------- */

// Clé/libellé d'état : le prix dépend fondamentalement de l'état de la carte,
// donc chaque état (une condition loose, ou un grader+grade gradée) a son
// propre mini-carnet bid/ask — pas un seul carnet global mélangeant tout.
function stateKeyOf(row: { grader: string; grade?: string | number | null; condition?: string | null }) {
  return row.grader === "RAW" ? `RAW:${row.condition}` : `${row.grader}:${row.grade}`;
}
function stateLabelOf(row: { grader: string; grade?: string | number | null; condition?: string | null }) {
  return row.grader === "RAW" ? `Loose · ${row.condition}` : `${row.grader} ${row.grade ?? ""}`.trim();
}

function groupOrderbookByState(wtb: WTBRow[], listings: ListingRow[]) {
  const map = new Map<string, { label: string; bids: WTBRow[]; asks: ListingRow[] }>();
  for (const w of wtb) {
    const key = stateKeyOf(w);
    if (!map.has(key)) map.set(key, { label: stateLabelOf(w), bids: [], asks: [] });
    map.get(key)!.bids.push(w);
  }
  for (const l of listings) {
    const key = stateKeyOf(l);
    if (!map.has(key)) map.set(key, { label: stateLabelOf(l), bids: [], asks: [] });
    map.get(key)!.asks.push(l);
  }
  return Array.from(map.values())
    .map((v) => ({
      label: v.label,
      bidCount: v.bids.length,
      askCount: v.asks.length,
      bestBid: v.bids.length ? Math.max(...v.bids.map((b) => Number(b.price_usdc))) : null,
      bestAsk: v.asks.length ? Math.min(...v.asks.map((a) => Number(a.price_usdc))) : null,
    }))
    // On n'affiche que les états qui ne sont pas vides (au moins un bid ou un ask).
    .filter((s) => s.bidCount + s.askCount > 0)
    .sort((a, b) => b.bidCount + b.askCount - (a.bidCount + a.askCount));
}

function Orderbook({
  listings,
  wtb,
  fmtMoney,
  loading,
}: {
  listings: ListingRow[];
  wtb: WTBRow[];
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  loading: boolean;
}) {
  const bestBidGlobal = wtb.length ? Math.max(...wtb.map((w) => Number(w.price_usdc))) : null;
  const bestAskGlobal = listings.length ? Math.min(...listings.map((l) => Number(l.price_usdc))) : null;
  const spread = bestBidGlobal != null && bestAskGlobal != null ? bestAskGlobal - bestBidGlobal : null;
  const spreadPct = bestAskGlobal && spread != null ? (spread / bestAskGlobal) * 100 : null;
  const states = useMemo(() => groupOrderbookByState(wtb, listings), [wtb, listings]);

  return (
    <Card>
      <CardHeader>
        <div>
          <CardTitle>Carnet d'ordres par état (USDC)</CardTitle>
          <CardDescription>
            {bestBidGlobal != null && bestAskGlobal != null ? (
              <>
                Meilleur bid <span className="font-mono text-success">{fmtMoney(bestBidGlobal, "USD")}</span>
                {" · "}Meilleur ask <span className="font-mono text-danger">{fmtMoney(bestAskGlobal, "USD")}</span>
                {spread != null ? <> · Spread <span className="font-mono">{fmtMoney(spread, "USD")} ({spreadPct?.toFixed(1)}%)</span></> : null}
              </>
            ) : (
              "Aucun ordre actif"
            )}
          </CardDescription>
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div className="overflow-hidden">
          <table className="w-full text-xs">
            <thead className="border-b border-border bg-surface-2/40 text-[10px] uppercase tracking-wide text-muted-fg">
              <tr>
                <th className="px-3 py-2 text-left">État</th>
                <th className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1 text-success"><span className="h-1.5 w-1.5 rounded-full bg-success" /> Best bid</span>
                </th>
                <th className="px-3 py-2 text-right">
                  <span className="inline-flex items-center gap-1 text-danger"><span className="h-1.5 w-1.5 rounded-full bg-danger" /> Best ask</span>
                </th>
                <th className="px-3 py-2 text-right">Ordres</th>
              </tr>
            </thead>
            <tbody className="[&_tr]:border-t [&_tr]:border-border/60">
              {loading && states.length === 0 ? (
                <tr><td className="p-4 text-center text-muted-fg" colSpan={4}><Loader2 className="inline h-3.5 w-3.5 animate-spin" /></td></tr>
              ) : states.length === 0 ? (
                <tr><td colSpan={4}><Empty title="Aucun carnet actif" description="Sois le premier à placer un ordre BUY ou SELL." /></td></tr>
              ) : (
                states.map((s) => (
                  <tr key={s.label}>
                    <td className="px-3 py-2 font-medium">{s.label}</td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-success">
                      {s.bestBid != null ? fmtMoney(s.bestBid, "USD") : <span className="text-muted-fg">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-danger">
                      {s.bestAsk != null ? fmtMoney(s.bestAsk, "USD") : <span className="text-muted-fg">—</span>}
                    </td>
                    <td className="px-3 py-2 text-right text-[10px] text-muted-fg">
                      {s.bidCount} buy · {s.askCount} sell
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}

/* ---------- Tables ---------- */
function PricesTable({
  rows,
  loading,
  fmtMoney,
  fmtDate,
}: {
  rows: PriceRow[];
  loading: boolean;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-hidden">
          <Table className="[&_th]:px-3 [&_td]:px-3">
            <THead>
              <TR>
                <TH>Source</TH>
                <TH className="hidden lg:table-cell">Version</TH>
                <TH className="hidden md:table-cell">Grading</TH>
                <TH className="text-right">Price</TH>
                <TH className="hidden sm:table-cell">Condition</TH>
                <TH className="hidden md:table-cell">Lang</TH>
                <TH className="text-right">Date</TH>
              </TR>
            </THead>
            <TBody>
              {loading && rows.length === 0 ? (
                <SkeletonRows rows={6} cols={7} />
              ) : rows.length === 0 ? (
                <tr><td colSpan={7}><Empty title="No price observations" /></td></tr>
              ) : (
                rows.map((r, i) => (
                  <TR key={i + ":" + (r.source ?? "")}>
                    <TD className="font-medium leading-tight">
                      {r.source_url ? (
                        <a
                          href={r.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={r.source_url_exact ? "Voir l'annonce d'origine" : "Voir la fiche produit (prix agrégé, pas une annonce précise)"}
                          className={cn(
                            "inline-flex items-center gap-1 hover:underline",
                            r.source_url_exact ? "text-fg" : "text-muted-fg",
                          )}
                        >
                          {r.source ?? "—"}
                          <ExternalLink className="h-3 w-3 shrink-0 opacity-70" />
                        </a>
                      ) : (
                        <span title="source sans lien">{r.source ?? "—"}</span>
                      )}
                    </TD>
                    <TD className="hidden text-xs text-muted-fg lg:table-cell">{r.version ?? "—"}</TD>
                    <TD className="hidden text-xs md:table-cell">{[r.grading_co, r.grade].filter(Boolean).join(" ") || "—"}</TD>
                    <TD className="whitespace-nowrap text-right font-mono">{r.price != null ? fmtMoney(r.price, r.currency || "EUR") : "—"}</TD>
                    <TD className="hidden text-xs sm:table-cell">{r.condition ?? "—"}</TD>
                    <TD className="hidden text-xs md:table-cell">{r.language ?? "—"}</TD>
                    <TD className="whitespace-nowrap text-right text-xs text-muted-fg">{fmtDate(r.price_date || r.created_at)}</TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}

function ListingsTable({
  rows,
  loading,
  fmtMoney,
  fmtDate,
}: {
  rows: ListingRow[];
  loading: boolean;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-hidden">
          <Table>
            <THead>
              <TR>
                <TH>État</TH>
                <TH>Cert</TH>
                <TH className="text-right">Price (USDC)</TH>
                <TH>Seller</TH>
                <TH>Published</TH>
              </TR>
            </THead>
            <TBody>
              {loading && rows.length === 0 ? (
                <SkeletonRows rows={3} cols={5} />
              ) : rows.length === 0 ? (
                <tr><td colSpan={5}><Empty title="No active listings" /></td></tr>
              ) : (
                rows.map((l, i) => (
                  <TR key={(l.id ?? i) + ":" + (l.cert_number ?? l.condition ?? "")}>
                    <TD><StateBadge grader={l.grader} grade={l.grade} condition={l.condition} /></TD>
                    <TD className="font-mono text-xs text-muted-fg">{l.cert_number ?? "—"}</TD>
                    <TD className="text-right font-mono">{fmtMoney(l.price_usdc, "USD")}</TD>
                    <TD className="font-mono text-xs" title={l.seller_wallet}>{shortAddr(l.seller_wallet)}</TD>
                    <TD className="text-xs text-muted-fg">{fmtDate(l.created_at)}</TD>
                  </TR>
                ))
              )}
            </TBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}

const ONCHAIN_TONE: Record<string, "warn" | "info" | "success" | "danger" | "neutral"> = {
  created: "warn",
  claimed: "info",
  released: "success",
  refunded: "danger",
  "off-chain": "neutral",
};

function WTBTable({
  rows,
  loading,
  fmtMoney,
  fmtDate,
}: {
  rows: WTBRow[];
  loading: boolean;
  fmtMoney: (v: number | string | null | undefined, c?: string) => string;
  fmtDate: (v: string | null | undefined) => string;
}) {
  return (
    <Card>
      <CardBody className="p-0">
        <div className="overflow-hidden">
          <Table>
            <THead>
              <TR>
                <TH>État</TH>
                <TH className="text-right">Price (USDC)</TH>
                <TH>Buyer</TH>
                <TH>On-chain</TH>
                <TH>Published</TH>
              </TR>
            </THead>
            <TBody>
              {loading && rows.length === 0 ? (
                <SkeletonRows rows={3} cols={5} />
              ) : rows.length === 0 ? (
                <tr><td colSpan={5}><Empty title="No buy offers" /></td></tr>
              ) : (
                rows.map((w, i) => {
                  const st = w.onchain_status || "off-chain";
                  return (
                    <TR key={(w.id ?? i) + ":" + w.buyer_wallet}>
                      <TD><StateBadge grader={w.grader} grade={w.grade} condition={w.condition} /></TD>
                      <TD className="text-right font-mono">{fmtMoney(w.price_usdc, "USD")}</TD>
                      <TD className="font-mono text-xs" title={w.buyer_wallet}>{shortAddr(w.buyer_wallet)}</TD>
                      <TD><Badge tone={ONCHAIN_TONE[st] || "neutral"} variant="soft">{st.replace("-", " ")}</Badge></TD>
                      <TD className="text-xs text-muted-fg">{fmtDate(w.created_at)}</TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
}

